/**
 * LatestLiveGiftInfoRemapper
 *
 * Parses Firestore wire JSON into a `LatestLiveGiftInfoDto`.
 *
 * Flutter source:
 *   - lib/data/models/latest_live_gift_info.dart (the Dart model)
 *   - lib/data/models/latest_live_gift_info.g.dart:9-16 (the generated factory)
 *
 * Defaults applied (per the generated factory):
 *   - `coins` defaults to `0`
 *   - `giftInstances` defaults to `1`
 *   - `userId` / `userName` / `giftName` / `giftThumbnail` default to `''`
 *
 * Why `giftInstances` defaults to `1` instead of `0` / `null`:
 *   Every read of `latestLiveGiftInfo.giftInstances` (Flutter:
 *   `video_call_screen.dart:678`, `live_streaming_screen.dart:1043`) gates on
 *   `giftInstances > 1` before rendering a quantity prefix. So the parsed value
 *   when the field is absent is semantically "don't render a quantity" — `0`,
 *   `null`, and `1` would all behave identically at every current read site.
 *   We keep the default at `1` for two reasons:
 *   (a) Flutter parity (`LatestLiveGiftInfo({this.giftInstances = 1})` on
 *       `latest_live_gift_info.dart:21`); any change here must land on Flutter too.
 *   (b) Historical Firestore docs that pre-date the field have no value for it,
 *       and a default of `1` honestly represents the historical baseline rather
 *       than an undercounted `0`. The cloud function (`send_gift.ts:108, 142`)
 *       always writes the field explicitly for newly-sent gifts, so this default
 *       only ever materializes for legacy documents.
 */

import type { LatestLiveGiftInfoDto } from '@/data/dtos/info/latestLiveGiftInfoDto';
import type { JsonObject } from '@/data/types/json';

export class LatestLiveGiftInfoRemapper {
  static fromJson(data: JsonObject): LatestLiveGiftInfoDto {
    return {
      coins: (data['coins'] as number | undefined) ?? 0,
      // Flutter default: LatestLiveGiftInfo({this.giftInstances = 1}). Kept by
      // design — see the file-level docblock for the rationale.
      giftInstances: (data['giftInstances'] as number | undefined) ?? 1,
      userId: (data['userId'] as string | undefined) ?? '',
      userName: (data['userName'] as string | undefined) ?? '',
      giftName: (data['giftName'] as string | undefined) ?? '',
      giftThumbnail: (data['giftThumbnail'] as string | undefined) ?? '',
    };
  }

  /**
   * Serialize a DTO back to the Firestore wire map. Inverse of `fromJson`;
   * emits every field explicitly, mirroring Flutter's generated `toJson`
   * (lib/data/models/latest_live_gift_info.g.dart:18-26) which always writes
   * all six keys in this order.
   */
  static toJson(dto: LatestLiveGiftInfoDto): JsonObject {
    return {
      coins: dto.coins,
      giftInstances: dto.giftInstances,
      userId: dto.userId,
      userName: dto.userName,
      giftName: dto.giftName,
      giftThumbnail: dto.giftThumbnail,
    };
  }
}
